import { Component, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NotificationService } from '@shared/services/notification.service';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { AttendanceDetailTotals, AttendanceRosterSummaryDto, DivineServiceDetailDto, MemberAttendanceDto, TotalsByCategoryData } from '../../models/divine-service.model';
import { DivineServiceApiService } from '../../services/api/divine-service-api.service';
import { DivineServiceStateService } from '../../services/state/divine-service-state.service';
import { DivineServiceState } from '../../models/divine-service-state.model';
import { DivineServiceHeaderComponent } from '../../components/divine-service-header/divine-service-header.component';
import { AttendanceComponent } from '../../components/attendance/attendance.component';
import { AttendanceTotalsDialogComponent, AttendanceTotalsDialogData } from '../../components/attendance-totals-dialog/attendance-totals-dialog.component';
import { MemberFilterHelper } from '../../helpers';
import { DeleteConfirmDialogComponent } from '../../../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-divine-service-home-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatExpansionModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    DivineServiceHeaderComponent,
    AttendanceComponent
  ],
  templateUrl: './divine-service-home-page.component.html',
  styleUrl: './divine-service-home-page.component.scss'
})
export class DivineServiceHomePageComponent implements OnInit, OnDestroy {
  state$: Observable<DivineServiceState>;

  service: DivineServiceDetailDto | null = null;
  isLoading = false;
  members: MemberAttendanceDto[] = [];
  attendanceSummary: AttendanceRosterSummaryDto | null = null;
  totalAdultsAttendance = 0;
  totalMinorsAttendance = 0;

  @Input() serviceId: string | null = null;
  @Input() updateUnitBreadcrumb = true;
  unitId: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private divineServiceService: DivineServiceApiService,
    private stateService: DivineServiceStateService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private breadcrumbService: BreadcrumbService
  ) {
    this.state$ = this.stateService.getState$();
  }

  ngOnInit(): void {
    const allParams = this.route.snapshot.pathFromRoot.reduce(
      (acc, r) => ({ ...acc, ...r.params }), {} as Record<string, string>
    );
    this.unitId = allParams['unit-id'] ?? null;

    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.serviceId = params['divine-service-id'] ?? this.serviceId;
        if (this.serviceId) {
          this.loadService();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadService(): void {
    if (!this.serviceId) return;

    this.isLoading = true;
    this.stateService.updateState({ isLoading: true });
    this.cdr.markForCheck();

    this.divineServiceService.getDivineService(this.serviceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (service) => {
          const isClosed = service.isClosed === true || !!service.closedAtUtc;
          service = { ...service, isClosed };
          this.service = service;
          const serviceDate = new Date(service.startAt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          this.breadcrumbService.setDynamicCrumb('divine-service-id', serviceDate);
          if (this.updateUnitBreadcrumb && service.organizationUnitName) {
            this.breadcrumbService.setDynamicCrumb('unit-id', service.organizationUnitName);
          }
          this.isLoading = false;
          this.stateService.updateState({
            id: service.id,
            isLoading: false,
            isEditMode: true,
            isNewService: false,
            isClosed,
            date: new Date(service.startAt),
            type: service.divineServiceTypeId,
            bibleText: service.bibleText || '',
            organizationUnitId: service.organizationUnitId,
            memberInCharge: service.memberInCharge || '',
            location: service.location || '',
            notes: service.notes || '',
            offeringsARS: service.offeringsARS || 0,
            offeringsUSD: service.offeringsUSD || 0,
            totalAdults: service.totalAdults || 0,
            totalMinors: service.totalMinors || 0,
            visitsAdults: service.visitsAdults || 0,
            visitsMinors: service.visitsMinors || 0,
            newAdults: service.newAdults || 0,
            newMinors: service.newMinors || 0
          });
          this.loadMembersWithAttendance();
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.stateService.updateState({ isLoading: false });
          this.notificationService.error('Error al cargar el servicio divino');
          this.cdr.markForCheck();
        }
      });
  }

  private loadMembersWithAttendance(): void {
    if (!this.serviceId) {
      return;
    }

    this.stateService.updateState({ isLoading: true });

    this.divineServiceService.getMembersWithAttendance(this.serviceId, 1, 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.members = response.items || [];
          this.attendanceSummary = response.summary;
          this.stateService.updateState({ isLoading: false });
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading members with attendance:', error);
          this.stateService.updateState({ isLoading: false });
          this.notificationService.error('Error al cargar la concurrencia');
          this.cdr.markForCheck();
        }
      });
  }

  onMembersUpdated(updatedMembers: MemberAttendanceDto[]): void {
    this.members = updatedMembers;
    const sealingCounts = MemberFilterHelper.getSealingAgeCounts(updatedMembers, true);
    this.attendanceSummary = {
      ...(this.attendanceSummary ?? this.emptyAttendanceSummary()),
      sealedAdults: sealingCounts.sealedAdults,
      sealedMinors: sealingCounts.sealedMinors,
      unsealedAdults: sealingCounts.unsealedAdults,
      unsealedMinors: sealingCounts.unsealedMinors
    };
    this.cdr.markForCheck();
  }

  onAttendanceTotalsChanged(totals: { adultsAttendance: number; minorsAttendance: number }): void {
    this.totalAdultsAttendance = totals.adultsAttendance;
    this.totalMinorsAttendance = totals.minorsAttendance;

    this.stateService.updateState({
      totalAdults: totals.adultsAttendance,
      totalMinors: totals.minorsAttendance
    });

    if (this.service) {
      this.service = {
        ...this.service,
        totalAdults: totals.adultsAttendance,
        totalMinors: totals.minorsAttendance
      };
    }

    this.cdr.markForCheck();
  }

  get totalsByCategoryData(): TotalsByCategoryData {
    const state = this.stateService.getState();
    const summary = this.attendanceSummary ?? this.emptyAttendanceSummary();

    return {
      sealedAdults: summary.sealedAdults,
      sealedMinors: summary.sealedMinors,
      unsealedAdults: summary.unsealedAdults,
      unsealedMinors: summary.unsealedMinors,
      visitsAdults: (state.visitsAdults || 0) + summary.nominalVisitsAdults,
      visitsMinors: (state.visitsMinors || 0) + summary.nominalVisitsMinors,
      newAdults: state.newAdults || 0,
      newMinors: state.newMinors || 0
    };
  }

  private emptyAttendanceSummary(): AttendanceRosterSummaryDto {
    return {
      sealedAdults: 0,
      sealedMinors: 0,
      unsealedAdults: 0,
      unsealedMinors: 0,
      nominalVisitsAdults: 0,
      nominalVisitsMinors: 0,
      visitsAdults: 0,
      visitsMinors: 0,
      newAdults: 0,
      newMinors: 0,
      totalAdults: 0,
      totalMinors: 0
    };
  }

  get summaryAdults(): number {
    const totals = this.totalsByCategoryData;
    return totals.sealedAdults + totals.unsealedAdults + totals.visitsAdults + totals.newAdults;
  }

  get summaryMinors(): number {
    const totals = this.totalsByCategoryData;
    return totals.sealedMinors + totals.unsealedMinors + totals.visitsMinors + totals.newMinors;
  }

  get summaryTotal(): number {
    return this.summaryAdults + this.summaryMinors;
  }

  get hasMoreInformation(): boolean {
    if (!this.service) {
      return false;
    }

    return !!this.service.memberInCharge ||
      !!this.service.location ||
      (this.service.offeringsARS ?? 0) > 0 ||
      (this.service.offeringsUSD ?? 0) > 0 ||
      !!this.service.notes;
  }

  openAttendanceTotals(): void {
    const state = this.stateService.getState();
    const data: AttendanceTotalsDialogData = {
      totals: this.totalsByCategoryData,
      aggregateVisitsAdults: state.visitsAdults || 0,
      aggregateVisitsMinors: state.visitsMinors || 0,
      readonly: state.isClosed
    };

    this.dialog.open<
      AttendanceTotalsDialogComponent,
      AttendanceTotalsDialogData,
      AttendanceDetailTotals | null
    >(AttendanceTotalsDialogComponent, modalDialogConfig({
      size: 'lg',
      layout: 'summary',
      data
    })).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.saveTotalChanges(result);
        }
      });
  }

  goBack(): void {
    this.breadcrumbService.back();
  }

  goToServiceList(): void {
    if (this.unitId) {
      this.router.navigate(['/units', this.unitId, 'divine-services']);
    } else {
      this.router.navigate(['/divine-services']);
    }
  }

  editService(): void {
    if (this.serviceId) {
      if (this.unitId) {
        this.router.navigate(['/units', this.unitId, 'divine-services', this.serviceId, 'edit']);
      } else {
        this.router.navigate(['/divine-services', this.serviceId, 'edit']);
      }
    }
  }

  deleteService(): void {
    if (!this.service || !this.serviceId) {
      return;
    }

    const serviceDate = new Date(this.service.startAt).toLocaleDateString('es-AR');
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
      data: {
        title: 'Eliminar Servicio Divino',
        message: `¿Está seguro de que desea eliminar el servicio divino del ${serviceDate}?`
      }
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result || !this.serviceId) {
          return;
        }

        this.isLoading = true;
        this.cdr.markForCheck();

        this.divineServiceService.deleteDivineService(this.serviceId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notificationService.success('Servicio divino eliminado correctamente');
              this.goToServiceList();
            },
            error: (error) => {
              console.error('Error deleting divine service:', error);
              this.notificationService.error('Error al eliminar el servicio divino');
              this.isLoading = false;
              this.cdr.markForCheck();
            }
          });
      });
  }

  closeAttendance(): void {
    if (!this.serviceId || this.service?.isClosed) {
      return;
    }

    this.dialog.open(ConfirmDialogComponent, modalDialogConfig({
      size: 'sm',
      layout: 'confirm',
      data: {
        title: 'Cerrar concurrencia',
        message: 'Luego de cerrar no se podrán modificar asistencias ni totales.',
        confirmText: 'Cerrar concurrencia',
        cancelText: 'Cancelar'
      }
    })).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmed => {
        if (confirmed) {
          this.confirmCloseAttendance();
        }
      });
  }

  private confirmCloseAttendance(): void {
    if (!this.serviceId) {
      return;
    }

    this.isLoading = true;
    this.stateService.updateState({ isLoading: true });
    this.cdr.markForCheck();

    this.divineServiceService.closeDivineService(this.serviceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Concurrencia cerrada');
          this.loadService();
        },
        error: (error) => {
          console.error('Error closing divine service attendance:', error);
          this.notificationService.error('Error al cerrar la concurrencia');
          this.isLoading = false;
          this.stateService.updateState({ isLoading: false });
          this.cdr.markForCheck();
        }
      });
  }

  private saveTotalChanges(totals: AttendanceDetailTotals): void {
    if (!this.serviceId) {
      return;
    }

    const state = this.stateService.getState();
    this.stateService.updateState({ isSaving: true });

    const updateDto = {
      startAt: state.date?.toISOString() || new Date().toISOString(),
      endAt: state.date?.toISOString() || new Date().toISOString(),
      divineServiceTypeId: state.type || 0,
      bibleText: state.bibleText || '',
      memberInCharge: state.memberInCharge || '',
      location: state.location || '',
      organizationUnitId: state.organizationUnitId || '',
      totalMinors: this.totalMinorsAttendance,
      totalAdults: this.totalAdultsAttendance,
      notes: state.notes || '',
      offeringsARS: state.offeringsARS || 0,
      offeringsUSD: state.offeringsUSD || 0,
      visitsAdults: totals.visitsAdults,
      visitsMinors: totals.visitsMinors,
      newAdults: totals.newAdults,
      newMinors: totals.newMinors
    };

    this.divineServiceService.updateDivineService(this.serviceId, updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedService) => {
          this.service = updatedService;
          this.stateService.updateState({
            isSaving: false,
            visitsAdults: totals.visitsAdults,
            visitsMinors: totals.visitsMinors,
            newAdults: totals.newAdults,
            newMinors: totals.newMinors
          });
          this.notificationService.success('Detalle de concurrencia guardado');
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error saving totals:', error);
          this.notificationService.error('Error al guardar los datos');
          this.stateService.updateState({ isSaving: false });
          this.cdr.markForCheck();
        }
      });
  }
}
